import Link from "next/link";

import { FC, useCallback, useState } from "react";
import * as React from "react";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useSelector } from "react-redux";

import { FormikProvider, useFormik, FormikConfig } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { Airdrops } from "@prisma/client";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { airdropSchema } from "validators/client/airdrop";

import { selectAddress, selectCustodialAddress } from "redux/slices/metamask";

import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import CustomOutlinedInput from "components/CustomOutlinedInput";
import CustomButton from "components/CustomButton";
import Recaptcha from "components/Recaptcha";

import { STORAGE } from "enums/storage";

import { API_ROUTES } from "config/routes";

import { setObjectItem } from "utils/storage";
import { formatAccount } from "utils/blockchain";

import fetcher from "lib/fetchJson";
interface AddressSubmission {
  walletAddress: string;
}

interface SectionAirdropProps {
  canMint: boolean;
  html: string;
  buttonText: string;
  successMessage: string;
  hash: string;
  owner: string | null;
  airdropInfo?: {
    isOutsideCustodial: boolean;
    nonCustodialAddress: string;
    isClaimed: boolean;
    info?: (Airdrops & { explorerLink: string }) | null;
  };
}

const SectionAirdrop: FC<SectionAirdropProps> = ({
  canMint,
  html,
  buttonText,
  successMessage,
  hash,
  owner,
  airdropInfo,
}) => {
  const [link, setLink] = useState<string>("");

  const { executeRecaptcha } = useGoogleReCaptcha();

  const metamaskAddress = useSelector(selectAddress)?.toLowerCase();
  const custodialAddress = useSelector(selectCustodialAddress)?.toLowerCase();

  const isLoggedAsOwner =
    owner?.toLowerCase() === metamaskAddress ||
    owner?.toLowerCase() === custodialAddress;

  const onSubmit: FormikConfig<AddressSubmission>["onSubmit"] = useCallback(
    async (values) => {
      try {
        if (!executeRecaptcha) {
          return Promise.reject({
            message: "Execute recaptcha not yet available",
          });
        }
        const captchaToken = await executeRecaptcha("CLAIM_AIRDROP");
        setObjectItem(STORAGE.AIRDROP, values);
        const data = await fetcher<{
          data: {
            explorerLink: string;
          };
        }>(
          API_ROUTES.AIRDROP,
          {
            ...values,
            hash,
            captchaToken,
          },
          "POST",
        );
        setLink(data.data.explorerLink);
      } catch (e) {
        toast.error(e.data.message ?? "Something went wrong.");
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executeRecaptcha, hash],
  );

  const initialState: AddressSubmission = {
    walletAddress: airdropInfo?.nonCustodialAddress ?? "",
  };

  const formik = useFormik({
    initialValues: initialState,
    validationSchema: toFormikValidationSchema(airdropSchema),
    validateOnChange: false,
    onSubmit: onSubmit,
  });

  if (!owner) {
    return (
      <InfoBox type={INFO_BOX_TYPE.INFO}>
        Please mint the item to claim the airdrop.
      </InfoBox>
    );
  }

  if (airdropInfo?.info?.explorerLink || link) {
    return (
      <Box>
        <InfoBox type={INFO_BOX_TYPE.SUCCESS}>
          {successMessage + " "}
          <Link
            href={airdropInfo?.info?.explorerLink ?? link}
            target="_blank"
            color="white"
          >
            {`View your transaction`}
          </Link>
        </InfoBox>
      </Box>
    );
  }

  if (
    !isLoggedAsOwner &&
    airdropInfo?.isOutsideCustodial &&
    airdropInfo.nonCustodialAddress &&
    !airdropInfo.isClaimed
  ) {
    return (
      <Box component="form" onSubmit={formik.handleSubmit}>
        <FormikProvider value={formik}>
          <Typography dangerouslySetInnerHTML={{ __html: html }} mb={2} />
          <Typography paragraph>
            Your airdrop will be sent to{" "}
            {formatAccount(airdropInfo.nonCustodialAddress)}
          </Typography>
          <Typography paragraph>
            If you would like to claim the airdrop to a different address,
            please log in as the owner of this item.
          </Typography>
          <CustomButton
            sx={{
              textTransform: "uppercase",
              ml: "auto",
              display: "block",
            }}
            type="submit"
            isLoading={formik.isSubmitting}
            disabled={formik.isSubmitting || !canMint}
            color="primary"
          >
            {buttonText}
          </CustomButton>
        </FormikProvider>
      </Box>
    );
  }

  if (isLoggedAsOwner) {
    return (
      <FormikProvider value={formik}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Typography dangerouslySetInnerHTML={{ __html: html }} mb={2} />
          <CustomOutlinedInput
            label="Wallet Address / ENS"
            inputProps={{
              id: "walletAddress",
              placeholder: "Wallet Address / ENS",
              value: formik.values.walletAddress,
              onChange: formik.handleChange,
            }}
            formControlProps={{
              fullWidth: true,
              margin: "normal",
            }}
            error={formik.errors.walletAddress}
          />
          <Recaptcha boxProps={{ mb: 2 }} />
          <CustomButton
            sx={{
              textTransform: "uppercase",
              ml: "auto",
              display: "block",
            }}
            type="submit"
            isLoading={formik.isSubmitting}
            disabled={formik.isSubmitting || !canMint}
            color="primary"
          >
            {buttonText}
          </CustomButton>
        </Box>
      </FormikProvider>
    );
  }

  return (
    <InfoBox type={INFO_BOX_TYPE.INFO}>
      Please log in as the owner of this item to claim the airdrop.
    </InfoBox>
  );
};

export default SectionAirdrop;
